import { useRef } from "react"
import type { MetaFunction } from "react-router"
import { Briefcase, CalendarDays, Clock, MapPin } from "lucide-react"

import { canonicalUrl, pageMeta, SITE_URL } from "~/lib/meta"
import { capture } from "~/client/posthog.client"
import { ButtonLink } from "~/components/ui/button"
import { H2, Lead, List, P, Strong } from "~/components/blog/prose"
import { BlogProse } from "~/components/blog/BlogLayout"
import { TableOfContents } from "~/components/blog/TableOfContents"
import { Header } from "~/components/landing/Header"
import { PageHero } from "~/components/landing/PageHero"
import { ForestBackdrop } from "~/components/landing/ForestBackdrops"
import { IconChip } from "~/components/landing/SectionKit"
import { Footer } from "~/components/landing/Footer"

// /jobs: a one-page posting for the open role, no index of roles. Copy is
// from the hiring doc Kjartan shared (Slack, 2026-10-21, "Data Scientist
// Hire - October 2026"), revised 2026-10-22, with typos fixed. Laid out like a
// blog post (see BlogLayout): hero and body share one 768px column so the
// title and text line up, the facts sit under a hairline where a post has
// its byline, the apply card takes BlogCta's slot after the intro, and the
// Contents rail hangs off the column's right edge from xl. Emits JobPosting
// JSON-LD so the role can show up in Google's job search. No "Get access"
// band: this page is for candidates, not buyers.
//
// Applications go to the jobs inbox by email, not the LinkedIn posting
// (Slack, 2026-10-22). The subject line tells the inbox which role it is; the
// address is also printed beside the buttons for visitors without a mail app.
const APPLY_EMAIL = "jobs@pine59.com"
const APPLY_HREF = `mailto:${APPLY_EMAIL}?subject=${encodeURIComponent(
  "Application: Data Scientist",
)}`

const JOB = {
  title: "Data Scientist",
  location: "Oslo, Norway",
  workplace: "On-site hybrid, 3-4 days a week in the office",
  team: "Data science",
  posted: "2026-10-21",
  postedDisplay: "October 21, 2026",
}

// Copy below marks emphasis with **double asterisks**: <Strong> on the page,
// <strong> in the JSON-LD. The bold on "Data Scientist" and "product
// mindset" is the doc's own.
const HOOK = "Are you a Data Scientist that likes building and owning products?"

// The doc's first paragraph, split after its first sentence so that sentence
// can open the body as the Lead.
const LEAD =
  "At Pine59, we are developing products for the rapidly growing data industry and the need to know how humans interact with the physical world."

const INTRO = [
  "We innovate to create products which leverage aggregated location data that helps explain human mobility. Our products are built to help with city planning, making decisions within retail and real estate, emergency planning and more. With the acceleration of AI, the opportunity to grow and develop a high quality location data product is optimal.",
  "As a result, we are not only developers, we are **inventors and product owners!** We wish to drive the industry forward by creating tools that have not been possible previously, due to new advances in cloud technology, AI, and aggregated location data.",
]

// The doc's call to action, carried by the apply card.
const JOIN = {
  title: "Do you want to take ownership and be part of that journey?",
  body: "Come and join us!",
}

const ROLE = [
  "At Pine59, we build products from some of the largest communications datasets available. This is a rapidly growing industry, and our product scope and reach is expanding to meet this demand. As a result, we are excited to be expanding our team, and are searching for an enthusiastic **Data Scientist** with a **product mindset** to join us.",
  "Our teams are multidisciplinary, with product, data engineering, data science, machine learning, and data analysis coming together in one team to develop competitive products. While closely collaborating with various teams and stakeholders within the company, we have the freedom of innovation and experimentation to develop high-quality and privacy-friendly location data insights that can change the location data industry for good.",
  "Specifically for the role we are looking for, you will develop and improve statistical data products used by clients around the world. Pine59 emphasizes anonymisation and privacy, ensuring the location events **never even have to leave a customer's servers** (thus ensuring security). It is important that you have a strong product and value oriented mindset and want to understand the true end value of what we are building.",
  "This is an on-site hybrid position (with **3-4 days in the week working in our Oslo office**, or more if you want!).",
  "Our main office is on Karl Johans Gate in the heart of Oslo, where we meet every week with our international colleagues. Being part of Pine59 means being part of a friendly and efficient workplace, with opportunities to grow and advance within the company.",
]

const SECTIONS = [
  {
    heading: "Key responsibilities",
    items: [
      "Be part of a team owning location data products end to end: methodology, validation, production pipelines, and the quality and usefulness of what clients receive",
      "Invent and improve the methods that turn vast quantities of anonymised location data into reliable aggregate data products",
      "Validate results against ground truth and external sources, quantify bias and uncertainty, and track down the cause when the numbers shift",
      "Build robust data pipelines in BigQuery SQL and Python, with monitoring that detects data drift and quality issues before they reach clients",
    ],
  },
  {
    heading: "Required qualifications",
    items: [
      "You have an academic degree equivalent to a Bachelor's or higher in a quantitative field such as statistics, mathematics, computer science, physics or engineering",
      "You have at least 2 years of professional experience in data science",
      "You have a strong ownership mentality and passion for what you build",
      "You have rich experience in using Python and SQL for data processing (preferably within the BigQuery dialect)",
      "You communicate clearly in English, both in writing and in person, and can explain technical results to people without a technical background",
      "You are a positive force. Adaptable and full of curiosity, you enjoy a high degree of collaboration and working in a team",
      "You are value oriented and want to understand how the products you build are used",
      "You have some basic Data Engineering skills (Airflow especially)",
      "You have experience working with geospatial data",
      "You are adept at using AI tooling to be effective",
    ],
  },
  {
    heading: "Most relevant technologies",
    items: [
      "BigQuery",
      "Python",
      "Git",
      "Apache Airflow",
      "Visualization tools: Kepler.gl, matplotlib, +",
    ],
  },
  {
    heading: "What we expect of you",
    items: [
      "We expect you to be positive-minded with a “can do” attitude to tackle challenges together with your team",
      "We are a startup, so things can change fast and you need to be agile and proactive on this journey",
      "We have customers using the product daily, so you will take part in an on-call rotation, during normal working hours only",
    ],
  },
]

/** Renders **marked** spans of a copy string as <Strong>. */
function rich(text: string): React.ReactNode {
  return text
    .split(/\*\*(.+?)\*\*/)
    .map((part, i) => (i % 2 ? <Strong key={i}>{part}</Strong> : part))
}

// Google wants the description as HTML; built from the same copy the page
// renders so the two never drift.
function jobPostingLd(url: string) {
  const html = (t: string) => t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
  const paragraphs = [
    HOOK,
    `${LEAD} ${INTRO[0]}`,
    ...INTRO.slice(1),
    `${JOIN.title} ${JOIN.body}`,
    ...ROLE,
  ].map((p) => `<p>${html(p)}</p>`)
  const sections = SECTIONS.map(
    (s) =>
      `<h3>${s.heading}</h3><ul>${s.items.map((i) => `<li>${i}</li>`).join("")}</ul>`,
  )
  return {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: JOB.title,
    description: [...paragraphs, ...sections].join(""),
    datePosted: JOB.posted,
    url,
    directApply: false,
    hiringOrganization: {
      "@type": "Organization",
      name: "Pine59",
      sameAs: SITE_URL,
      logo: `${SITE_URL}/brand/logo-black.svg`,
    },
    jobLocation: {
      "@type": "Place",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Karl Johans Gate 21",
        postalCode: "0159",
        addressLocality: "Oslo",
        addressCountry: "NO",
      },
    },
  }
}

export const meta: MetaFunction = ({ location }) =>
  pageMeta({
    title: "Data Scientist · Jobs · Pine59",
    description:
      "Pine59 is hiring a Data Scientist to own location data products end to end, from methodology and validation to production pipelines. Oslo, hybrid.",
    pathname: location.pathname,
    extra: [
      { "script:ld+json": jobPostingLd(canonicalUrl(location.pathname)) },
    ],
  })

function ApplyButton({ location }: { location: "inline" | "end" }) {
  return (
    <ButtonLink
      href={APPLY_HREF}
      surface="dark"
      onClick={() =>
        capture("job_apply_clicked", { job: "data-scientist", location })
      }
    >
      Apply by email
    </ButtonLink>
  )
}

function ApplyAddress({ className = "" }: { className?: string }) {
  return (
    <p className={`text-[14px] leading-relaxed text-cream/60 ${className}`}>
      Send your application to{" "}
      <a
        href={APPLY_HREF}
        className="font-medium text-chartreuse underline underline-offset-2 transition-colors hover:text-hay"
      >
        {APPLY_EMAIL}
      </a>
    </p>
  )
}

// In a post's byline slot: the role's facts, under the same hairline.
function Facts() {
  const rows = [
    { icon: MapPin, label: "Location", value: JOB.location },
    { icon: Clock, label: "Workplace", value: JOB.workplace },
    { icon: Briefcase, label: "Team", value: JOB.team },
    { icon: CalendarDays, label: "Posted", value: JOB.postedDisplay },
  ]
  return (
    <dl className="mb-8 grid gap-x-10 gap-y-5 border-b border-line pb-6 sm:grid-cols-2">
      {rows.map(({ icon, label, value }) => (
        <div key={label} className="flex items-center gap-3.5">
          <IconChip icon={icon} />
          <div>
            <dt className="u-eyebrow">{label}</dt>
            <dd className="mt-0.5 font-sans text-[15px] leading-snug text-ink">
              {value}
            </dd>
          </div>
        </div>
      ))}
    </dl>
  )
}

// Same card as the blog's BlogCta (forest, topo texture, one action), pointed
// at the application instead of /get-started.
function ApplyCard() {
  return (
    <aside
      aria-label="Apply for this role"
      className="relative mt-10 overflow-hidden rounded-xl bg-forest px-6 py-7 sm:px-8 sm:py-8"
    >
      <ForestBackdrop lazy />
      <div className="relative sm:flex sm:items-end sm:justify-between sm:gap-8">
        <div className="max-w-md">
          <p className="font-sans text-[22px] font-semibold leading-snug tracking-tight text-hay">
            {JOIN.title}
          </p>
          <p className="mt-2 text-[16px] leading-relaxed text-cream/75">
            {JOIN.body}
          </p>
          <ApplyAddress className="mt-1" />
        </div>
        <div className="mt-5 shrink-0 sm:mt-0">
          <ApplyButton location="inline" />
        </div>
      </div>
    </aside>
  )
}

export default function Jobs() {
  const bodyRef = useRef<HTMLDivElement>(null)

  return (
    <div className="bg-paper">
      <Header />
      <PageHero eyebrow="We are hiring" title={JOB.title} maxWidth="max-w-3xl">
        <p className="mt-3 max-w-xl text-[17px] leading-relaxed text-paper/70">
          {HOOK}
        </p>
      </PageHero>

      <div className="py-14 sm:py-16">
        {/* Mirrors BlogLayout: this wrapper is BlogProse's box without its
            gutter (BlogProse supplies that), so the text lines up with the
            hero title, and the Contents rail sits just past its right edge. */}
        <div className="relative mx-auto w-full max-w-3xl">
          <aside className="absolute inset-y-0 left-full hidden w-60 xl:block">
            <TableOfContents containerRef={bodyRef} />
          </aside>

          <div ref={bodyRef}>
            <BlogProse>
              <Facts />

              <Lead>{LEAD}</Lead>
              {INTRO.map((p) => (
                <P key={p}>{rich(p)}</P>
              ))}

              <ApplyCard />

              <H2>The role</H2>
              {ROLE.map((p) => (
                <P key={p}>{rich(p)}</P>
              ))}

              {SECTIONS.map((s) => (
                <section key={s.heading}>
                  <H2>{s.heading}</H2>
                  <List items={s.items} />
                </section>
              ))}
            </BlogProse>
          </div>
        </div>
      </div>

      <section className="relative overflow-hidden bg-forest px-6 py-20 text-center">
        <ForestBackdrop lazy />
        <div className="relative">
          <img
            src="/brand/logo-symbol-green.svg"
            alt=""
            width={102}
            height={42}
            loading="lazy"
            className="mx-auto h-6 w-auto"
          />
          <h2 className="mx-auto mt-5 max-w-2xl font-sans text-[clamp(32px,5vw,56px)] font-normal leading-none tracking-[-0.04em] text-hay">
            We are hiring a{" "}
            <span className="font-serif italic">Data Scientist</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[17px] leading-relaxed text-cream/75">
            Pine59 is an equal opportunity employer and we encourage you to
            apply.
          </p>
          <div className="mt-8">
            <ApplyButton location="end" />
          </div>
          <ApplyAddress className="mt-4" />
        </div>
      </section>

      <Footer closingCta={false} />
    </div>
  )
}
